import { AnyAction } from 'redux';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching } from 'app/types-shared';
import { STATE } from 'app/constants';
import { complexValidationFromServer } from 'app/infrastructure/data/validation';
import { downloadFile } from 'app/infrastructure/platform/file';
import { buildDatedFilename } from 'app/infrastructure/filename/build-dated-filename';
import { ErrorNotification, InfoNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import { isForbiddenStatus, blockAccess, isUnprocessableEntity } from 'app/area/shared/actions-utils';
import { defaultMaxResults } from '../constants';
import {
  RequestMetadata, ReceiveMetadata, FailMetadata,
  ClearList,
  RequestList, ReceiveList, FailList,
  ModifyItem, CancelChanges,
  RequestSaveChanges, ReceiveSaveChanges, FailSaveChanges,
  ValidationChange,
  RequestExportExcel, ReceiveExportExcel, FailExportExcel,
  OpenExcelImport, CloseExcelImport,
  RequestImportExcel, ReceiveImportExcel, FailImportExcel,
  ClearImportValidationErrors
} from './action-types';
import { blockAccessTranslation, ListType } from './constants';
import { ListActionCreator, ListApiGateway } from './types';

// eslint:disable-next-line: max-func-body-length
export const actionCreatorsFactory = <METADATA, ITEM_DATA, MODIFIED_DATA, SEARCH_MODEL>(
  listType: ListType,
  apiGateway: ListApiGateway<METADATA, ITEM_DATA, MODIFIED_DATA, SEARCH_MODEL>
): ListActionCreator<MODIFIED_DATA, SEARCH_MODEL> => ({

  getMetadata: (): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      try {
        dispatch(new RequestMetadata(listType));

        const metadata = await apiGateway.getMetadata();

        dispatch(new ReceiveMetadata<METADATA>(listType, metadata));
      } catch (err) {
        dispatch(new FailMetadata(listType));

        if (err?.userMessages?.length ?? 0 > 0) {
          dispatch(new ErrorNotification(err.userMessages));
        }
      }
    },

  clearList: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      const requestId = Date.now();
      dispatch(new ClearList(listType, requestId));
    },

  loadTextsList: (
    model: SEARCH_MODEL,
    startIndex: number = 0,
    maxResults: number = defaultMaxResults
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      const requestId = Date.now();
      dispatch(new RequestList<SEARCH_MODEL>(listType, requestId, model, startIndex === 0));

      try {
        const textsList = await apiGateway.getList(startIndex, maxResults, model);

        dispatch(new ReceiveList<ITEM_DATA>(listType, requestId, textsList.results, textsList.totalNumberOfResults));
      } catch (err) {

        const displayExceededLengthError = isUnprocessableEntity(err);

        !displayExceededLengthError && err?.userMessages && dispatch(new ErrorNotification(err.userMessages));

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.textConfiguration,
            translationId: blockAccessTranslation(listType).id
          });
        }

        dispatch(new FailList(listType, requestId, displayExceededLengthError
          ? (Array.isArray(err?.userMessages)
            ? err?.userMessages?.map(m => m.message)
            : [err?.userMessages])
          : undefined));
      }
    },

  modifyText: (
    modifiedData: MODIFIED_DATA
  ): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ModifyItem<MODIFIED_DATA>(listType, modifiedData));
    },

  cancelChanges: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new CancelChanges(listType));
    },

  saveChanges: (
    modifiedData: MODIFIED_DATA[],
    successMessage: string,
    validationErrorMessage: string
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      try {
        dispatch(new RequestSaveChanges(listType));

        await apiGateway.saveChanges(modifiedData);

        dispatch(new ReceiveSaveChanges(listType));
        dispatch(new SuccessNotification(successMessage));
      } catch (err) {
        dispatch(new FailSaveChanges(listType));

        if (err?.userMessages?.length ?? 0 > 0) {
          dispatch(new ErrorNotification(err.userMessages));
        }

        if (Object.keys(err?.validationErrors ?? {}).length > 0) {
          dispatch(new ErrorNotification(validationErrorMessage));
          dispatch(new ValidationChange(listType, complexValidationFromServer(err.validationErrors)));
        }
      }
    },

  displayError: (errorMessage: string): ActionDispatching<any, any> =>
    (dispatch, getState) => {
      dispatch(new ErrorNotification(errorMessage));
    },

  exportToExcel: (
    model: SEARCH_MODEL,
    maxResults: number,
    filename: string,
    startMessage: string,
    successMessage: string
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {
      const startIndex = 0;

      dispatch(new RequestExportExcel(listType));
      dispatch(new SuccessNotification(startMessage));

      try {
        const response = await apiGateway.exportToExcel(startIndex, maxResults, model);
        downloadFile(response.blob, buildDatedFilename(filename, 'xlsx'));

        dispatch(new SuccessNotification(successMessage));
        dispatch(new ReceiveExportExcel(listType));

      } catch (err) {
        dispatch(new FailExportExcel(listType));
        err?.userMessages && dispatch(new ErrorNotification(err.userMessages));
      }
    },

  openExcelImport: (): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new OpenExcelImport(listType));
    },

  closeExcelImport: (): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new CloseExcelImport(listType));
      dispatch(new ClearImportValidationErrors(listType));
    },

  clearImportValidationErrors: (): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new ClearImportValidationErrors(listType));
    },

  saveImport: (
    file: File,
    startMessage: string,
    successMessage: string,
    errorMessage: string,
    errorHandler: () => void,
    reloadFile: string
  ):
    ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {
      dispatch(new RequestImportExcel(listType));
      dispatch(new InfoNotification(startMessage));

      try {
        const xhr = apiGateway.createUploadRequest();
        const data = new FormData();
        data.append('file', file, file.name);

        xhr.onload = () => {
          if (xhr.status === 200 || xhr.status === 204) {
            dispatch(new SuccessNotification(successMessage));
            dispatch(new ReceiveImportExcel(listType));
          } else {
            const error = JSON.parse(xhr?.response);
            const isErrorsList = Array.isArray(error);
            dispatch(new FailImportExcel(listType, error && isErrorsList ? error : undefined));
            error && !isErrorsList && error?.messages?.userMessages
              ? error.messages.userMessages.forEach(err => dispatch(new ErrorNotification(err.message)))
              : dispatch(new ErrorNotification(errorMessage));
          }
        };
        xhr.onerror = () => {
          errorHandler();
          dispatch(new FailImportExcel(listType, undefined));
          dispatch(new ErrorNotification(reloadFile));
        };
        xhr.send(data);

      } catch (err) {
        const isErrorsList = Array.isArray(err);
        !isErrorsList && err?.messages?.userMessages && dispatch(new ErrorNotification(err.messages.userMessages));
        dispatch(new FailImportExcel(listType, err && isErrorsList ? err : undefined));
      }
    }
});
